import { Box, Typography } from '@mui/material';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { BookingFlowResumer } from '../../../booking-flow/BookingFlowResumer';
import { BookingFlowStepper } from '../../../booking-flow/BookingFlowStepper';
import { useAuthStore } from '../../../auth/authStore';
import { ApprovalPassengerStep } from '../../../components/booking/ApprovalPassengerStep';
import {
  approvalEmployeeId,
  corporateMaxBudget,
  needsCorporateApproval,
} from '../../../components/booking/corporateApproval';
import { useSentForApproval } from '../../../components/booking/useSentForApproval';
import { buildHotelGuestSlots, hotelFlowConfig, type HotelFlowState } from '../flowConfig';
import { hotelResumeAdapter } from '../resume';
import { SearchStep } from '../components/SearchStep';
import { ResultsStep } from '../components/ResultsStep';
import { GuestsStep } from '../components/GuestsStep';
import { ReservationStep } from '../components/ReservationStep';
import { PaymentStep } from '../components/PaymentStep';
import { DocumentStep } from '../components/DocumentStep';

function HotelFlowInner() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);

  useSentForApproval({
    sent: !!flow.state.sentForApproval,
    idle: flow.status === 'idle',
    currentStepId: flow.currentStep?.id,
    passengerStepId: 'guests',
    reservationPath: '/hotel/reservations',
    reset: flow.reset,
    message:
      'Otel rezervasyonu onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  });

  // Allocate sonrası sepette EmployeeID (ya da aramada seçili personel) var + rezervasyon
  // bütçe/politika dışı → misafir adımı personel kaydından salt-okunur "Onaya Gönder"e döner.
  const gate = {
    cart: flow.state.shoppingCart,
    searchEmployeeId: flow.state.searchRequest?.employeeID,
  };
  const approvalEmpId = approvalEmployeeId(gate, sessionEmployeeId);
  const singleGuest = buildHotelGuestSlots(flow.state.searchRequest).length === 1;
  const guestStepIsApproval = singleGuest && needsCorporateApproval(gate) && !!approvalEmpId;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <BookingFlowStepper />
      {flow.stepIndex === 0 && <SearchStep />}
      {flow.stepIndex === 1 && <ResultsStep />}
      {flow.stepIndex === 2 &&
        (guestStepIsApproval ? (
          <ApprovalPassengerStep
            employeeId={approvalEmpId as number}
            maxBudget={corporateMaxBudget(gate)}
            productLabel="otel rezervasyonu"
            loading={flow.status === 'running'}
            onSubmit={(guests, contact, billingInformation) => {
              flow.runStep({ guests, contact, billingInformation, sentForApproval: true }).catch(() => undefined);
            }}
          />
        ) : (
          <GuestsStep />
        ))}
      {flow.stepIndex === 3 && <ReservationStep />}
      {flow.stepIndex === 4 && <PaymentStep />}
      {flow.stepIndex === 5 && <DocumentStep />}
    </Box>
  );
}

export default function HotelFlowPage() {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Otel Rezervasyonu
      </Typography>
      <BookingFlowResumer config={hotelFlowConfig} adapter={hotelResumeAdapter}>
        <HotelFlowInner />
      </BookingFlowResumer>
    </Box>
  );
}
